<template>
  <div class="login-container">
    <div class="background-shapes">
      <div class="shape shape-1"></div>
      <div class="shape shape-2"></div>
      <div class="shape shape-3"></div>
    </div>

    <div class="login-card">
      <div class="login-header">
        <div class="logo-container">
          <img src="/images/blocks/logos/icono.svg" alt="Logo" class="logo-image" />
        </div>
        <h1 class="login-title">¡Bienvenido!</h1>
        <p class="login-subtitle">Ingrese sus credenciales para acceder al sistema</p>
      </div>

      <Message v-if="sessionTerminated" severity="warn" class="mb-3" :closable="true" @close="resetSessionTerminated">
        <div class="flex align-items-center">
          <i class="pi pi-exclamation-triangle mr-2"></i>
          <span>Tu sesión fue cerrada porque iniciaste sesión en otro dispositivo.</span>
        </div>
      </Message>

      <Message v-if="serverError" severity="error" class="mb-3" :closable="false">
        {{ serverError }}
      </Message>

      <form @submit.prevent="handleLogin" class="login-form-content">
        <div class="field">
          <strong class="input-label">
            <i class="pi pi-user mr-2"></i>
            USUARIO O EMAIL:
          </strong>
          <InputText
            id="login"
            v-model="form.login"
            type="text"
            class="custom-input"
            placeholder="username@ejemplo.com"
            :class="{ 'p-invalid': fieldErrors.login }"
            :disabled="loading"
            autocomplete="username"
          />
          <small v-if="fieldErrors.login" class="error-message">
            <i class="pi pi-exclamation-circle mr-1"></i>
            {{ fieldErrors.login }}
          </small>
        </div>

        <div class="field">
          <strong class="input-label">
            <i class="pi pi-lock mr-2"></i>
            CONTRASEÑA:
          </strong>
          <Password
            id="password"
            v-model="form.password"
            class="custom-password"
            :class="{ 'p-invalid': fieldErrors.password }"
            placeholder="••••••••"
            :feedback="false"
            :disabled="loading"
            toggleMask
          />
          <small v-if="fieldErrors.password" class="error-message">
            <i class="pi pi-exclamation-circle mr-1"></i>
            {{ fieldErrors.password }}
          </small>
        </div>

        <Button
          type="submit"
          label="INICIAR SESIÓN"
          class="login-button"
          :loading="loading"
          :disabled="loading"
          icon="pi pi-sign-in"
        />
      </form>

      <div class="login-footer">
        <p class="footer-text">
          <strong>Desarrollado por: SIT-BOLIVIA</strong> |
          <strong>Soporte:</strong>
          <a href="https://wa.me/59163084946" target="_blank" class="footer-link">
            <i class="pi pi-phone mr-1"></i>63084946
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useAuth } from '@/stores'
import { useToast } from 'primevue/usetoast'
import { onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import Password from 'primevue/password'

const auth = useAuth()
const toast = useToast()
const route = useRoute()

const loading = ref(false)
const serverError = ref('')
const fieldErrors = ref({})
const sessionTerminated = ref(false)

const form = reactive({
  login: '',
  password: ''
})

onMounted(() => {
  if (route.query.reason === 'session_terminated') {
    sessionTerminated.value = true
    toast.add({
      severity: 'warn',
      summary: 'Sesión cerrada',
      detail: 'Tu sesión fue cerrada porque iniciaste sesión en otro dispositivo.',
      life: 5000
    })
  }

  if (auth.sessionTerminated) {
    sessionTerminated.value = true
    auth.resetSessionTerminated()
  }
})

const resetSessionTerminated = () => {
  sessionTerminated.value = false
  auth.resetSessionTerminated()
  if (route.query.reason === 'session_terminated') {
    window.history.replaceState({}, '', window.location.pathname)
  }
}

const clearErrors = () => {
  serverError.value = ''
  fieldErrors.value = {}
}

const handleLogin = async () => {
  if (loading.value) return

  loading.value = true
  clearErrors()

  try {
    if (!form.login.trim()) {
      fieldErrors.value.login = 'El email o username es requerido'
      loading.value = false
      return
    }
    if (!form.password) {
      fieldErrors.value.password = 'La contraseña es requerida'
      loading.value = false
      return
    }
    if (form.password.length < 8) {
      fieldErrors.value.password = 'La contraseña debe tener al menos 8 caracteres'
      loading.value = false
      return
    }

    await auth.userLogin({
      login: form.login.trim(),
      password: form.password
    })

    toast.add({
      severity: 'success',
      summary: 'Éxito',
      detail: 'Sesión iniciada correctamente',
      life: 3000
    })

    form.login = ''
    form.password = ''
  } catch (error) {
    console.error('Error en login:', error)
    serverError.value = error.message

    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.message,
      life: 5000
    })
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #2c5f7c 0%, #1a3a4d 100%);
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.background-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
}

.shape-1 {
  width: 350px;
  height: 350px;
  top: -100px;
  right: -100px;
}

.shape-2 {
  width: 250px;
  height: 250px;
  bottom: 80px;
  left: -80px;
}

.shape-3 {
  width: 180px;
  height: 180px;
  bottom: -60px;
  right: 15%;
}

.login-card {
  background: #ffffff;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 450px;
  position: relative;
  z-index: 1;
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-header {
  text-align: center;
  margin-bottom: 1rem;
}

.logo-container {
  margin-bottom: 1rem;
}

.logo-image {
  width: 150px;
  height: 120px;
  object-fit: contain;
}

.login-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #2c5f7c;
}

.login-subtitle {
  color: #6c757d;
  font-size: 0.95rem;
}

.field {
  margin-bottom: 1rem;
}

.input-label {
  display: flex;
  align-items: center;
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

:deep(.custom-input) {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-size: 1rem;
}

:deep(.custom-input:focus) {
  border-color: #2c5f7c;
  box-shadow: 0 0 0 3px rgba(44, 95, 124, 0.1);
  outline: none;
}

:deep(.custom-input.p-invalid) {
  border-color: #dc3545;
}

:deep(.custom-password) {
  width: 100%;
}

:deep(.custom-password .p-password-input) {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
}

:deep(.custom-password .p-password-input:focus) {
  border-color: #2c5f7c;
  box-shadow: 0 0 0 3px rgba(44, 95, 124, 0.1);
}

.error-message {
  display: flex;
  align-items: center;
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 0.3rem;
}

.login-button {
  width: 100%;
  padding: 0.875rem;
  background: linear-gradient(135deg, #2c5f7c, #1a3a4d);
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.05rem;
  transition: all 0.3s ease;
  color: #ffffff;
}

.login-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(44, 95, 124, 0.4);
  background: linear-gradient(135deg, #1a3a4d, #2c5f7c);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-footer {
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #dee2e6;
}

.footer-text {
  color: #6c757d;
  font-size: 0.875rem;
  margin: 0;
}

.footer-link {
  color: #2c5f7c;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.footer-link:hover {
  color: #1a3a4d;
  text-decoration: underline;
}

@media (max-width: 480px) {
  .login-card {
    padding: 2rem 1.5rem;
  }

  .login-title {
    font-size: 1.75rem;
  }

  .logo-image {
    width: 100px;
    height: 100px;
  }
}
</style>